import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Phone, ChevronDown } from "lucide-react";
import { FAQS, CONTACT_PHONE, CONTACT_WHATSAPP, LEAD_CHANNELS } from "../../constants/data";
import { saveLeadToAPI, faqsAPI } from "../../services/api";

const GOLD = "#50C878";

// ─── CHATBOT FLOW ────────────────────────────────────────────
// greeting → collecting (full name → phone → email)
//   • EVERY field can be skipped by typing "skip" or tapping Skip.
// → faq mode: the bot answers ONLY from the FAQ knowledge base
//   (loaded live from /api/faqs, with the local FAQS list as a
//   fallback). Anything it can't match is handed to a live agent.
// ─────────────────────────────────────────────────────────────

const BOT_DELAY = 600;

function BotMessage({ text, isBot = true }) {
  return (
    <div style={{ display: "flex", justifyContent: isBot ? "flex-start" : "flex-end", marginBottom: "12px" }}>
      {isBot && (
        <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "linear-gradient(135deg, #50C878, #7FD9A0)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginRight: "8px", marginTop: "2px", fontSize: "12px", fontWeight: "bold", color: "#FFFFFF" }}>
          GB
        </div>
      )}
      <div
        style={{
          maxWidth: "80%",
          padding: "10px 14px",
          borderRadius: isBot ? "4px 12px 12px 12px" : "12px 4px 12px 12px",
          background: isBot ? "linear-gradient(135deg, #FFFFFF, #E2F1E7)" : "linear-gradient(135deg, #50C878, #7FD9A0)",
          border: isBot ? "1px solid rgba(80,200,120,0.25)" : "none",
          color: isBot ? "rgba(1,50,32,0.95)" : "#FFFFFF",
          fontFamily: "'Inter', sans-serif",
          fontSize: "0.85rem",
          lineHeight: 1.55,
          whiteSpace: "pre-wrap",
        }}
      >
        {text}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
      <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "linear-gradient(135deg, #50C878, #7FD9A0)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "bold", color: "#FFFFFF" }}>GB</div>
      <div style={{ display: "flex", gap: "4px", padding: "10px 14px", background: "linear-gradient(135deg, #FFFFFF, #E2F1E7)", border: "1px solid rgba(80,200,120,0.25)", borderRadius: "4px 12px 12px 12px" }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ width: "6px", height: "6px", borderRadius: "50%", background: GOLD, animation: `bounce 1.2s ${i * 0.2}s infinite` }} />
        ))}
      </div>
      <style>{`@keyframes bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }`}</style>
    </div>
  );
}

// Score-based FAQ matcher. Returns the best FAQ whose keywords/words
// overlap the query, or null if nothing meaningful matches.
function matchFAQ(query, faqList) {
  const q = query.toLowerCase();
  const qWords = q.split(/[^a-z0-9]+/).filter((w) => w.length > 2);
  let best = null;
  let bestScore = 0;

  for (const faq of faqList) {
    const keywords = faq.keywords || [];
    let score = 0;
    for (const k of keywords) {
      const kw = String(k).toLowerCase();
      if (q.includes(kw)) score += 3;                       // full keyword phrase present
      else if (qWords.some((w) => kw.includes(w) || w.includes(kw))) score += 1;
    }
    // Light match against the question text itself.
    const questionWords = (faq.question || "").toLowerCase().split(/[^a-z0-9]+/);
    score += qWords.filter((w) => questionWords.includes(w)).length * 0.5;

    if (score > bestScore) {
      bestScore = score;
      best = faq;
    }
  }
  return bestScore >= 1 ? best : null;
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState("idle"); // idle | greeting | collecting | faq
  const [typing, setTyping] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [tempData, setTempData] = useState({});
  const [collectStep, setCollectStep] = useState(0);
  // FAQ knowledge base — starts from local FAQS, replaced by live /api/faqs if reachable.
  const [faqList, setFaqList] = useState(FAQS);
  const messagesEndRef = useRef(null);

  const collectFields = [
    { key: "fullName", label: "your full name", placeholder: "e.g. Rajesh Kumar (or tap Skip)" },
    { key: "phone", label: "your 10-digit mobile number", placeholder: "e.g. 9876543210" },
    { key: "email", label: "your email address", placeholder: "e.g. rajesh@email.com (or tap Skip)" },
  ];

  // Load the live FAQ knowledge base once (admin-managed in the dashboard).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await faqsAPI.list();
        const rows = res?.data || [];
        if (alive && rows.length) setFaqList(rows);
      } catch (_) {
        // Keep the bundled FAQS fallback if the API is unreachable.
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  function addBotMessage(text, delay = BOT_DELAY) {
    setTyping(true);
    return new Promise((resolve) => {
      setTimeout(() => {
        setTyping(false);
        setMessages((prev) => [...prev, { id: Date.now() + Math.random(), isBot: true, text }]);
        resolve();
      }, delay);
    });
  }

  function addUserMessage(text) {
    setMessages((prev) => [...prev, { id: Date.now() + Math.random(), isBot: false, text }]);
  }

  function pushCallCTA() {
    setMessages((prev) => [...prev, { id: Date.now() + Math.random(), isBot: true, type: "call_cta" }]);
  }

  async function startChat() {
    setMessages([]);
    setTempData({});
    setCollectStep(0);
    setStep("greeting");
    await addBotMessage("👋 Welcome to GroupBuy Flats! I'm here to help you find your dream property in Hyderabad.", 400);
    await addBotMessage(`Could you please share ${collectFields[0].label}?\n\nEvery detail is optional — just tap "Skip" or type "skip" if you'd rather not.`, 900);
    setStep("collecting");
    setCollectStep(0);
  }

  // Handle one collection step. `skipped` is true when the user
  // tapped the Skip button (no value entered).
  async function handleCollect(rawValue, skipped = false) {
    const field = collectFields[collectStep];
    const value = (rawValue || "").trim();
    const isSkip = skipped || value.toLowerCase() === "skip" || value === "";

    if (!isSkip) {
      // Validate phone only when a value was actually provided.
      if (field.key === "phone" && !/^[6-9]\d{9}$/.test(value.replace(/\s/g, ""))) {
        await addBotMessage('That doesn\'t look like a valid 10-digit Indian mobile number. Please re-enter it, or tap "Skip".');
        return;
      }
      if (field.key === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        await addBotMessage('That email doesn\'t look right. Please re-enter it, or tap "Skip".');
        return;
      }
    }

    const updated = { ...tempData, [field.key]: isSkip ? "" : value };
    setTempData(updated);

    const nextStep = collectStep + 1;
    if (nextStep < collectFields.length) {
      setCollectStep(nextStep);
      const next = collectFields[nextStep];
      await addBotMessage(`Great! Now, could you share ${next.label}?`);
    } else {
      await completeLeadCapture(updated);
    }
  }

  async function completeLeadCapture(data) {
    // The backend requires at least a name + valid phone to store a lead.
    // If the visitor skipped the phone, we don't attempt a save — we just
    // continue helping them, and they can reach an agent any time.
    const hasSavableLead = data.fullName && /^[6-9]\d{9}$/.test((data.phone || "").replace(/\s/g, ""));
    if (hasSavableLead) {
      await saveLeadToAPI({
        first_name: data.fullName,
        last_name: "",
        phone: data.phone,
        email: data.email || "",
        channel: LEAD_CHANNELS.CHATBOT,
      });
    }

    setStep("faq");
    const firstName = data.fullName ? data.fullName.split(" ")[0] : "there";
    const reachLine = hasSavableLead
      ? `I've noted your details and our associate will reach out to you on ${data.phone}.`
      : `No problem — you can share your details with us any time.`;

    await addBotMessage(
      `Thanks, ${firstName}! 🙏\n\n${reachLine}\n\nAsk me anything about GroupBuy Flats — pricing, site visits, home loans, legal checks, and more. Or tap below to talk to a live associate.`
    );
    pushCallCTA();
  }

  async function handleFAQ(query) {
    const lower = query.toLowerCase();

    // Explicit request to reach a person.
    if (/\b(associate|agent|talk|call|speak|human|representative|contact)\b/.test(lower)) {
      await addBotMessage(
        `Connecting you to an associate! Call or WhatsApp us at:\n\n📞 +91 ${CONTACT_PHONE}\n\nOur team is available Mon–Sat, 9am–7pm.`
      );
      pushCallCTA();
      return;
    }

    const faq = matchFAQ(query, faqList);
    if (faq) {
      await addBotMessage(faq.answer);
      await addBotMessage("Is there anything else I can help you with?", 400);
      return;
    }

    // FAQ-ONLY policy: the bot never invents answers. When it can't
    // match a question, it hands off to a live associate.
    await addBotMessage(
      "I can only answer questions I've been trained on, and I don't have an answer for that one yet. Our associate will be happy to help!"
    );
    await addBotMessage(
      "You can also ask me about: how group buying works, discounts, available properties, site visits, home loans, or legal/RERA checks.",
      500
    );
    pushCallCTA();
  }

  async function handleSend() {
    const val = inputValue.trim();
    if (!val) return;
    setInputValue("");
    addUserMessage(val);
    if (step === "collecting") await handleCollect(val);
    else if (step === "faq") await handleFAQ(val);
  }

  async function handleSkip() {
    if (step !== "collecting") return;
    const field = collectFields[collectStep];
    addUserMessage(`Skip ${field.key === "fullName" ? "name" : field.key}`);
    setInputValue("");
    await handleCollect("", true);
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <>
      {/* FAB */}
      <button
        onClick={() => {
          setOpen(!open);
          if (!open && step === "idle") startChat();
        }}
        aria-label="Open chat"
        style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 9990, width: "56px", height: "56px", borderRadius: "50%", background: "linear-gradient(135deg, #50C878, #7FD9A0)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 20px rgba(80,200,120,0.5)", transition: "transform 0.2s" }}
        onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.08)")}
        onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        {open ? <X size={22} color="#FFFFFF" /> : <MessageCircle size={22} color="#FFFFFF" />}
      </button>

      {/* Chat window */}
      {open && (
        <div style={{ position: "fixed", bottom: "90px", right: "24px", zIndex: 9991, width: "360px", maxWidth: "calc(100vw - 48px)", height: "520px", maxHeight: "calc(100vh - 120px)", borderRadius: "16px", background: "linear-gradient(180deg, #FFFFFF 0%, #F7FBF8 100%)", border: "1px solid rgba(80,200,120,0.3)", boxShadow: "0 20px 60px rgba(0,0,0,0.7)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Header */}
          <div style={{ padding: "16px 20px", background: "linear-gradient(135deg, #50C878, #7FD9A0)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <p style={{ margin: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: "1.1rem", fontWeight: 700, color: "#FFFFFF" }}>GroupBuy Flats</p>
              <p style={{ margin: 0, fontSize: "0.72rem", color: "rgba(26,18,6,0.7)", fontFamily: "'Inter', sans-serif" }}>
                <span style={{ width: "6px", height: "6px", background: "#2d8a4e", borderRadius: "50%", display: "inline-block", marginRight: "4px" }} />
                Online — typically replies instantly
              </p>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: "rgba(0,0,0,0.15)", border: "none", borderRadius: "50%", width: "28px", height: "28px", cursor: "pointer", color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ChevronDown size={16} />
            </button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px", scrollbarWidth: "thin", scrollbarColor: "rgba(80,200,120,0.2) transparent" }}>
            {messages.map((msg) =>
              msg.type === "call_cta" ? (
                <div key={msg.id} style={{ display: "flex", justifyContent: "flex-start", marginBottom: "12px" }}>
                  <div style={{ marginLeft: "36px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <a href={`tel:+91${CONTACT_PHONE}`} style={{ padding: "8px 16px", background: "linear-gradient(135deg, #50C878, #7FD9A0)", borderRadius: "20px", color: "#FFFFFF", textDecoration: "none", fontSize: "0.82rem", fontWeight: 700, display: "flex", alignItems: "center", gap: "6px" }}>
                      <Phone size={13} /> Call Now
                    </a>
                    <a href={CONTACT_WHATSAPP} target="_blank" rel="noopener noreferrer" style={{ padding: "8px 16px", background: "#25D366", borderRadius: "20px", color: "#013220", textDecoration: "none", fontSize: "0.82rem", fontWeight: 700 }}>
                      WhatsApp
                    </a>
                  </div>
                </div>
              ) : (
                <BotMessage key={msg.id} text={msg.text} isBot={msg.isBot} />
              )
            )}
            {typing && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(80,200,120,0.15)", display: "flex", gap: "8px", alignItems: "center" }}>
            {step === "collecting" && (
              <button
                onClick={handleSkip}
                style={{ flexShrink: 0, padding: "8px 12px", background: "transparent", border: `1px solid ${GOLD}`, borderRadius: "20px", color: GOLD, fontFamily: "'Inter', sans-serif", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}
              >
                Skip
              </button>
            )}
            <input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={step === "collecting" ? collectFields[collectStep]?.placeholder || "Type here..." : "Ask a question..."}
              style={{ flex: 1, background: "rgba(80,200,120,0.10)", border: "1px solid rgba(80,200,120,0.25)", borderRadius: "20px", padding: "9px 16px", color: "#013220", fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", outline: "none" }}
              onFocus={(e) => (e.target.style.borderColor = GOLD)}
              onBlur={(e) => (e.target.style.borderColor = "rgba(80,200,120,0.25)")}
            />
            <button onClick={handleSend} style={{ width: "36px", height: "36px", borderRadius: "50%", background: "linear-gradient(135deg, #50C878, #7FD9A0)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Send size={15} color="#FFFFFF" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
